import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { authApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import { ErrorBanner, InfoBanner } from "../components/Feedback";
import { Icon } from "../components/Icon";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/board";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setNeedsVerification(false);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setNeedsVerification(true);
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : "Login failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setInfo(null);
    setError(null);
    try {
      const res = await authApi.resend(email);
      setInfo(res.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resend verification.");
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <span className="brand-mark">
          <Icon name="layers" size={22} />
        </span>
        Ticket Tracker
      </div>

      <div className="auth-card">
        <div className="auth-head">
          <h1>Log in</h1>
          <p>Use your verified account.</p>
        </div>

        <form onSubmit={onSubmit}>
          {error && <ErrorBanner message={error} />}
          {info && <InfoBanner message={info} />}

          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              placeholder="name@example.com"
              value={email}
              required
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Password</span>
            <div className="input-affix">
              <input
                type={show ? "text" : "password"}
                value={password}
                required
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="affix-btn"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
              >
                <Icon name={show ? "eyeOff" : "eye"} size={18} />
              </button>
            </div>
          </label>

          <button type="submit" className="btn-primary btn-block" disabled={busy}>
            {busy ? "Logging in…" : "Log in"}
          </button>

          {needsVerification && (
            <div className="stack" style={{ gap: 8, alignItems: "center" }}>
              <span className="auth-alt">Account not verified?</span>
              <button type="button" className="secondary btn-block" onClick={resend}>
                Resend verification email
              </button>
            </div>
          )}
        </form>

        <div className="auth-divider">or</div>
        <p className="auth-alt">
          <Link to="/signup">Create an account →</Link>
        </p>
      </div>
    </div>
  );
}
